import React, { useEffect, useState } from 'react';
import axiosInstance from '../../services/axiosInstance';
import {
  showSuccessToast,
  showErrorToast,
  showWarnToast,
} from '../../utils/toast';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../../store/authSlice';
import { authStorage } from '../../utils/authStorage';
import FileUploadField from '../CommonComponents/FileUploadField';

const AddCustomerModal = ({ editData, onClose, onUserAddedOrUpdated }) => {
  // Customer name and system type fields
  const [name, setName] = useState('');
  const [systemType, setSystemType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Logo file + preview (optional)
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const dispatch = useDispatch();

  // Prefill form if editing an existing customer
  useEffect(() => {
    if (editData) {
      setName(editData.name ?? '');
      setSystemType(editData.system_type ?? '');
      // If editing and logo exists, show it as preview (URL already normalized by backend)
      setLogoPreview(editData.logo || null);
    } else {
      setName('');
      setSystemType('');
      setLogoFile(null);
      setLogoPreview(null);
    }
  }, [editData]);

  const handleLogoChange = (file) => {
    setLogoFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setLogoPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      // If cleared, keep existing preview only when editing and user didn't explicitly remove
      setLogoPreview(editData?.logo || null);
    }
  };

  const handleSubmit = async () => {
    if (!name || !systemType) {
      showWarnToast('Please fill out all fields.');
      return;
    }

    setIsSubmitting(true);

    const method = editData ? 'put' : 'post';
    const url = editData
      ? `/superadmin/customers/${editData.id}`
      : `/superadmin/customers`;

    try {
      // Always use multipart so backend can accept optional file under field name `logo`
      const formData = new FormData();
      formData.append('name', name);
      formData.append('system_type', systemType);
      formData.append('status', 'ACTIVE');
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const response = await axiosInstance.request({
        method,
        url,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Merge logo into customer object for localStorage and Redux
      let updatedCustomer;
      if (response.data?.customer) {
        updatedCustomer = response.data.customer;
        if (response.data.logo) updatedCustomer.logo = response.data.logo;
      } else {
        // If backend only returns logo and message, merge with existing customer
        const prevCustomer = JSON.parse(localStorage.getItem('customer')) || {};
        updatedCustomer = { ...prevCustomer };
        if (response.data.logo) updatedCustomer.logo = response.data.logo;
      }
      // Save to localStorage (scoped by customer_id)
      if (updatedCustomer.id) {
        // Use authStorage.setCustomerData to ensure only one customer_* key exists
        authStorage.setCustomerData(updatedCustomer.id, updatedCustomer);
      }
      // Update Redux auth.user if logged in as admin
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        // Only update if user_type is admin (1) and customer_id matches
        if (userObj.user_type === 1 && userObj.customer_id === updatedCustomer.id) {
          const updatedUser = { ...userObj, logo: updatedCustomer.logo };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          dispatch(loginSuccess({ token: localStorage.getItem('token'), user: updatedUser }));
        }
      }

      showSuccessToast(editData ? 'Customer updated successfully!' : 'Customer added successfully!');
      onUserAddedOrUpdated(updatedCustomer); // Pass updated customer if needed
      onClose(); // optionally close modal after success
    } catch (error) {
      console.error('Error saving customer:', error);
      const msg = error.response?.data?.message || error.response?.data?.error || 'Something went wrong while saving the customer.';
      showErrorToast(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl p-6 shadow-xl max-w-md w-full">

        {/* Modal title */}
        <h2 className="text-xl font-semibold text-[#0f1e40] mb-6">
          {editData ? 'Edit Customer' : 'Add Customer'}
        </h2>

        {/* Customer name input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
            placeholder="Enter customer name"
            disabled={isSubmitting}
            style={{
              background: 'var(--color-bg)',
              color: 'var(--color-primary)',
              boxShadow: undefined
            }}
            onFocus={e => {
              e.target.style.borderColor = '#000';
              e.target.style.boxShadow = '0 0 0 2px #000';
            }}
            onBlur={e => {
              e.target.style.borderColor = 'var(--color-border)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        {/* System Type dropdown */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">System Type</label>
          <select
            value={systemType}
            onChange={(e) => setSystemType(e.target.value)}
            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2"
            disabled={isSubmitting}
            style={{
              background: '#fff',
              color: '#222',
              boxShadow: undefined
            }}
            onFocus={e => {
              e.target.style.borderColor = '#000';
              e.target.style.boxShadow = '0 0 0 2px #888';
            }}
            onBlur={e => {
              e.target.style.borderColor = 'var(--color-border)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value="">Select system type</option>
            <option value="Banking">Banking</option>
            <option value="Retail">Retail</option>
            <option value="E-commerce">E-commerce</option>
            <option value="Point of Sales">Point of Sales</option>
          </select>
        </div>

        {/* Logo upload (optional) */}
        <div className="mb-5">
          <FileUploadField
            label="Logo"
            accept="image/*"
            value={logoFile}
            preview={logoPreview}
            onChange={handleLogoChange}
            onRemove={() => { setLogoFile(null); setLogoPreview(null); }}
            optional={true}
            disabled={isSubmitting}
          />
        </div>

        {/* Action buttons */}
        <div className="flex justify-center gap-4 mt-2">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-[100px] h-[38px] border border-[#0f1e40] bg-white text-[#0f1e40] rounded-md text-sm font-medium focus:ring-2 focus:ring-[#0f1e40] focus:outline-none transition duration-150"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`w-[100px] h-[38px] rounded-md text-sm font-medium text-white transition ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'btn-primary'}
              `}
          >
            {editData ? 'Update' : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCustomerModal;
